import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchMySimpleInOutAvailability,
  fetchMySimpleInOutStatus,
  updateMySimpleInOutStatus,
  type SimpleInOutAvailabilityPayload,
  type SimpleInOutMeStatusPayload,
  type SimpleInOutStatusValue,
} from '../../api/simpleInOut';
import useSessionStore from '../../state/session';
import { useToasts } from '../../ui/ToastProvider';
import { safeErrorMessage } from '../../utils/appErrors';

/**
 * The signed-in person's own Simple I/O status, shared by the Home card, the
 * Home dot toggle, the account-menu section and the My status modal. The query
 * keys match SimpleInOutHeaderStatus, so a change shows in the top bar at once.
 * See docs/simple-inout-integration.md.
 */

export const SIMPLE_INOUT_STATUS_OPTIONS: Array<{ value: SimpleInOutStatusValue; label: string }> = [
  { value: 'in', label: 'In' },
  { value: 'out', label: 'Out' },
  { value: 'remote', label: 'Remote' },
  { value: 'busy', label: 'Busy' },
  { value: 'break', label: 'Break' },
  { value: 'sick', label: 'Sick' },
  { value: 'vacation', label: 'Vacation' },
];

export const SIMPLE_INOUT_QUICK_STATUS_OPTIONS = SIMPLE_INOUT_STATUS_OPTIONS.filter((option) => (
  option.value === 'in' || option.value === 'out' || option.value === 'busy'
));

export const SIMPLE_INOUT_AVAILABILITY_QUERY_KEY = ['simple-inout', 'me', 'availability'];
export const SIMPLE_INOUT_STATUS_QUERY_KEY = ['simple-inout', 'me', 'status'];

const STAFF_LEVELS = ['user', 'manager', 'admin', 'super_admin'];

const DISABLED_AVAILABILITY: SimpleInOutAvailabilityPayload = {
  ok: true,
  enabled: false,
  verified: false,
  verified_at: null,
  checked_at: null,
  token_source: 'none',
};

export async function fetchMySimpleInOutAvailabilityQuietly(): Promise<SimpleInOutAvailabilityPayload> {
  try {
    return await fetchMySimpleInOutAvailability();
  } catch (error) {
    const status = (error as { status?: number } | null)?.status;
    if (status === 400 || status === 401 || status === 403 || status === 404 || status === 409 || status === 503) {
      return {
        ...DISABLED_AVAILABILITY,
        last_error: {
          status,
          message: error instanceof Error ? error.message : null,
        },
      };
    }
    throw error;
  }
}

export function simpleInOutStatusLabel(status: string | null | undefined): string {
  const match = SIMPLE_INOUT_STATUS_OPTIONS.find((option) => option.value === status);
  return match?.label || (status ? status : 'Not set');
}

export function formatSimpleInOutEpoch(value: number | null | undefined): string {
  if (!value) return '--';
  const date = new Date(value * 1000);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString();
}

/** "8:04 AM" today, "10/12 8:04 AM" on another day. */
export function formatSimpleInOutEpochShort(value: number | null | undefined): string {
  if (!value) return '--';
  const date = new Date(value * 1000);
  if (Number.isNaN(date.getTime())) return '--';
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (date.toDateString() === new Date().toDateString()) return time;
  return `${date.toLocaleDateString([], { month: 'numeric', day: 'numeric' })} ${time}`;
}

export function formatSimpleInOutIso(value: string | null | undefined): string {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString();
}

export function simpleInOutLookupMessage(payload: SimpleInOutMeStatusPayload | null | undefined, error: unknown): string {
  const status = payload?.last_error?.status ?? ((error as { status?: number } | null)?.status ?? null);
  if (status === 429) return 'Simple I/O is rate limited';
  if (status) return `Simple I/O lookup failed (${status})`;
  return safeErrorMessage(error, 'Simple I/O lookup unavailable');
}

export function useMySimpleInOutStatus() {
  const level = useSessionStore((state) => state.securityLevel);
  const staff = STAFF_LEVELS.includes(level || '');
  const { push } = useToasts();
  const queryClient = useQueryClient();

  const availabilityQuery = useQuery({
    queryKey: SIMPLE_INOUT_AVAILABILITY_QUERY_KEY,
    queryFn: fetchMySimpleInOutAvailabilityQuietly,
    enabled: staff,
    staleTime: 60_000,
    retry: false,
  });
  const enabled = staff && availabilityQuery.data?.enabled === true;

  const statusQuery = useQuery({
    queryKey: SIMPLE_INOUT_STATUS_QUERY_KEY,
    queryFn: fetchMySimpleInOutStatus,
    enabled,
    refetchInterval: 60_000,
    retry: false,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (input: { status: SimpleInOutStatusValue; comment?: string }) => (
      updateMySimpleInOutStatus(input.status, input.comment || '')
    ),
    onSuccess: (payload) => {
      queryClient.setQueryData(SIMPLE_INOUT_STATUS_QUERY_KEY, payload);
      push({ type: 'success', message: 'Simple In/Out status updated' });
    },
    onError: (error) => {
      push({
        type: 'error',
        message: safeErrorMessage(error, 'Failed to update Simple In/Out status'),
      });
      void queryClient.invalidateQueries({ queryKey: SIMPLE_INOUT_AVAILABILITY_QUERY_KEY });
    },
  });

  const setStatus = React.useCallback(function setMySimpleInOutStatus(status: SimpleInOutStatusValue, comment?: string) {
    mutate({ status, comment });
  }, [mutate]);

  const payload = statusQuery.data;
  const unavailable = statusQuery.isError || payload?.unavailable === true;
  const ready = enabled && !availabilityQuery.isLoading && !availabilityQuery.isError;
  // Hidden when Simple I/O is off for this person, or they have no Simple I/O user.
  const visible = ready && (statusQuery.isLoading || unavailable || (Boolean(payload) && payload?.mapped !== false));
  const loaded = Boolean(payload?.mapped && !unavailable);

  return {
    visible,
    loaded,
    loading: statusQuery.isLoading,
    unavailable,
    stale: payload?.stale === true,
    payload,
    error: statusQuery.error,
    currentStatus: String(payload?.status?.status || '').toLowerCase(),
    comment: payload?.status?.comment || '',
    automatic: payload?.status?.automatic === true,
    updatedAt: payload?.status?.created_at ?? null,
    setStatus,
    pending: isPending,
  };
}
